/**
 * Sprint 28 · Home Décor category expansion — 26 new category pages.
 * Each entry drives /categories/home-decor/<slug>. Hand-written, zero
 * duplicates of existing products. Rendered by HomeDecorCategory.jsx.
 *
 * Shape:
 *   { slug, group, title, metaDescription, h1, eyebrow, lede,
 *     primaryKeyword, secondaryKeywords[], subcategories[],
 *     materials[], finishes[], clusters[{id,note}], services[{id,label}],
 *     uniqueSections:[{h2, body:[paragraphs]}], faqs:[[q,a]] }
 *
 * The template adds four shared sections at render time:
 *   • Manufacturing clusters (data-driven)
 *   • Product development, sampling, QC & compliance
 *   • International buyer notes (US · UK · EU · AU · ZA · combined)
 *   • Enquiry CTA
 */

export const HOME_DECOR_GROUPS = [
  { id: "decorative",   label: "Decorative objects" },
  { id: "candles",      label: "Candles & home fragrance" },
  { id: "tabletop",     label: "Tabletop & dining" },
  { id: "garden",       label: "Garden & outdoor" },
  { id: "textiles",     label: "Soft furnishings" },
  { id: "bathroom",     label: "Bathroom & wellness" },
  { id: "seasonal",     label: "Seasonal décor" },
];

/** Shared market context used by the combined international-buyer section. */
export const MARKET_NOTES = {
  us: "US retailers and private-label brands pull Indian décor via East Coast (Elizabeth, Savannah, New York) and West Coast (Long Beach, Oakland) at 32-45 day transit. CPSIA, Prop 65 and UL (for anything electrified) are standard; FSC and GOTS where retailer-mandated.",
  uk: "UK retailers import FOB Mundra / Nhava Sheva to Felixstowe or Southampton (28-32 days). UKCA, UK REACH and FSC Chain-of-Custody are our default documentation package; Lion Mark where children-facing.",
  eu: "Continental retailers consolidate to Rotterdam, Hamburg, Antwerp or Le Havre (26-30 days). CE where applicable, REACH SVHC, LFGB for food contact and EUDR for timber are coordinated factory-side; German Lieferkettengesetz chain-of-custody paperwork on request.",
  au: "AU/NZ buyers route via Mumbai or Chennai to Sydney, Melbourne, Brisbane and Auckland on direct Indian Ocean lanes (18-24 days). ISPM-15 heat-treated wood packing and AS/NZS compliance are default; AI-ECTA preferential tariff where applicable.",
  za: "South African importers route Nhava Sheva → Durban (14-18 days — the fastest major corridor from India). NRCS Letter of Authority / SABS where mandated; onward distribution to Johannesburg, Cape Town and Southern Africa partners.",
};

export const HOME_DECOR_CATEGORIES = [
  // ───────────────────────── A · DECORATIVE OBJECTS ─────────────────────────
  {
    slug: "vases",
    group: "decorative",
    title: "Vase Sourcing from India | Brass, Ceramic, Glass & Marble Vases | Asia Sourcing",
    metaDescription:
      "Vase sourcing from India for international retailers and interior brands. Brass, ceramic, hand-blown glass and marble vases from Moradabad, Khurja, Firozabad and Agra — supplier verification, product development, QC and export documentation.",
    eyebrow: "Home Décor · Decorative objects",
    h1: "Vase sourcing from India for retailers, brands & interior studios.",
    primaryKeyword: "Vase sourcing India",
    secondaryKeywords: ["Decorative vases India", "Brass vases India", "Ceramic vases India", "Glass vases wholesale India", "Marble vases India", "Hand-blown glass vases India"],
    lede: "India is the single largest exporter of decorative vases in the home-and-lifestyle category globally — Moradabad for brass and metal, Khurja and Jaipur for ceramic, Firozabad for hand-blown glass, Agra for white marble inlay. Asia Sourcing runs the full programme — supplier identification, prototype development, sampling, in-line and pre-shipment QC, export documentation and container loading — for international retailers, DTC brands and interior studios.",
    subcategories: [
      "Brass vases · antique, patinated, matte-black, nickel finishes",
      "Hand-painted ceramic vases · Khurja and Jaipur traditions",
      "Hand-blown glass vases · Firozabad clear, smoked and coloured glass",
      "Marble vases · white marble and marble with semi-precious inlay from Agra",
      "Terracotta vases · hand-thrown, slip-glazed, matte-finish",
      "Mother-of-pearl inlaid resin vases",
      "Rattan and wicker-wrapped vases",
    ],
    materials: ["Brass", "Ceramic", "Hand-blown glass", "White marble", "Terracotta", "Mother-of-pearl inlay", "Rattan", "Wicker", "Bone china"],
    finishes: ["Antique brass", "Patinated", "Hammered", "Matte-black", "Hand-painted", "Reactive glaze", "Clear and smoked glass", "Marble inlay with semi-precious stone"],
    clusters: [
      { id: "moradabad", note: "brass and metal vases" },
      { id: "firozabad", note: "hand-blown glass vases" },
      { id: "jaipur", note: "hand-painted ceramic and blue-pottery vases" },
      { id: "agra", note: "white marble and inlay vases" },
    ],
    services: [
      { id: "product-development-india", label: "Product development & sampling" },
      { id: "quality-control-india", label: "AQL 2.5 / 4.0 inspection" },
      { id: "export-documentation-india", label: "Export documentation" },
    ],
    uniqueSections: [
      { h2: "Why buyers source vases from India",
        body: [
          "Vases carry two SKU-level risks that programmes often underestimate: breakage rates and finish consistency. Our standard engagement spec defuses both — individually honeycomb-wrapped pieces inside corrugated inserts, master-carton drop tests at 80 cm per side, and finish-match checks against the signed PPS panel every production run. 20-foot FCLs typically clear with sub-1% in-transit breakage on the lines we supervise.",
          "The second underestimated risk is category-level knock-off exposure. Our development team watermark-marks and SKU-numbers every new mould or master pattern on signed PO, and we audit for cross-sale to competing buyers at random every quarter — a hygiene step surprisingly few agents operate.",
        ],
      },
      { h2: "Price bands and MOQ reality",
        body: [
          "Entry FOB India for a decorative brass vase sits around $4-$9 depending on height and complexity; hand-blown glass $2-$6; marble $12-$35 depending on inlay density; hand-painted ceramic $3-$12. MOQ per SKU is factory- and finish-dependent — brass starts at 60-120 pieces, ceramic at 200, hand-blown glass at 300 (one glory-hole batch), marble at 50. We routinely consolidate smaller MOQs across a mixed container for first-order buyers.",
        ],
      },
    ],
    faqs: [
      ["What is the minimum order quantity for brass vases from India?", "Factory-dependent, but 60-120 pieces per SKU is the standard entry MOQ for brass vases in Moradabad. For a mixed container, we consolidate SKUs across suppliers to clear a 20-foot FCL — typical first-order containers blend 15-30 vase SKUs."],
      ["Can Asia Sourcing develop a custom vase from a sketch or reference?", "Yes. Our development workflow is: brief → CAD → tooling or master pattern → first physical sample against the signed CAD → finish-approval panels → pre-production sample (PPS). Lead time brief-to-PPS is 4-7 weeks for brass and ceramic, 6-10 weeks for marble and hand-blown glass."],
      ["Which Indian clusters produce the best vases?", "Moradabad for brass and metalware, Firozabad for hand-blown glass, Khurja and Jaipur for ceramic, Agra for white marble and inlay. All four are documented on our clusters pages with buyer visit protocols."],
      ["How do you prevent breakage during shipping?", "Individually honeycomb-wrapped pieces inside corrugated inserts, standardised master-carton drop testing per PPS, and container-loading supervision photographed and signed for every FCL. Programmes we supervise typically clear sub-1% in-transit breakage."],
    ],
  },
  {
    slug: "decorative-trays",
    group: "decorative",
    title: "Decorative Tray Sourcing from India | Brass, Wood, Marble & Enamel | Asia Sourcing",
    metaDescription:
      "Decorative tray sourcing from India. Brass, enamel-painted, mango-wood, marble-inlay and camel-bone inlay trays from Moradabad, Jodhpur and Agra — supplier verification, QC and export documentation for international retailers.",
    eyebrow: "Home Décor · Decorative objects",
    h1: "Decorative tray sourcing from India.",
    primaryKeyword: "Decorative tray sourcing India",
    secondaryKeywords: ["Decorative trays wholesale India", "Brass trays India", "Mango wood trays India", "Marble trays India", "Enamel trays India", "Camel bone inlay trays India"],
    lede: "Decorative trays are one of India's deepest export categories — brass round and rectangular trays from Moradabad, mango-wood serving trays from Saharanpur, white-marble and inlay trays from Agra, enamel-painted and bone-inlay trays from Jaipur. Asia Sourcing manages development, sampling, QC and documentation for retail programmes running from a single container to full private-label collections.",
    subcategories: [
      "Brass trays · round, oval, rectangular; etched, hammered, patinated",
      "Mango-wood and acacia serving trays with brass or leather handles",
      "White marble and inlay trays from Agra",
      "Enamel-painted trays · Jaipur meenakari tradition",
      "Camel-bone inlay trays",
      "Mother-of-pearl inlaid resin trays",
      "Rattan and wicker trays",
    ],
    materials: ["Brass", "Mango wood", "Acacia wood", "White marble", "Enamel on brass", "Camel bone", "Mother-of-pearl inlay", "Rattan"],
    finishes: ["Antique brass", "Etched", "Hammered", "Natural wood oil", "Burnt-wood", "Marble with semi-precious inlay", "Hand-painted meenakari"],
    clusters: [
      { id: "moradabad", note: "brass and metalware trays" },
      { id: "saharanpur", note: "carved and hand-finished mango-wood trays" },
      { id: "agra", note: "marble and marble-inlay trays" },
      { id: "jaipur", note: "enamel and bone-inlay trays" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
      { id: "export-documentation-india", label: "Export documentation" },
    ],
    uniqueSections: [
      { h2: "Depth of category",
        body: [
          "Trays are a programme category — most buyers run 15-40 SKUs across sizes, shapes and finishes rather than three or four hero pieces. A typical first-container programme blends two brass round sizes, one brass rectangular, two wood sizes with contrasting handle treatments, and one or two marble or enamel accent SKUs. Entry FOB India from $3 (small brass) to $25 (large marble inlay).",
        ],
      },
      { h2: "The handle question",
        body: [
          "The single most-common defect we catch at PSI is loose handles — screw tightness during production falls below spec when line pace increases toward end-of-week. Our DPI protocol locks a torque-check gate at 30% completion; our PSI runs a lift test on every sampled unit. Buyers who skip DPI on wood-tray programmes see 2-4× the handle-failure rate on arrival.",
        ],
      },
    ],
    faqs: [
      ["What's the MOQ for mango-wood trays from India?", "Typically 150-300 pieces per SKU per size; smaller for mixed-SKU private-label programmes consolidated across a single factory."],
      ["Can you source trays with custom branding or laser engraving?", "Yes. Laser engraving on brass and wood, blind embossing on leather handles, hand-painted enamel branding on meenakari pieces. We develop the branding spec in sampling before PO."],
      ["Are mango-wood trays sustainable?", "Mango wood is harvested from end-of-fruit-life trees — functionally a by-product. FSC certification is available on request from our FSC-licensed sawmills in Saharanpur and Jodhpur."],
    ],
  },
  {
    slug: "decorative-bowls",
    group: "decorative",
    title: "Decorative Bowl Sourcing from India | Brass, Marble, Ceramic & Wood | Asia Sourcing",
    metaDescription:
      "Decorative bowl sourcing from India. Brass, hammered-metal, marble, ceramic and hand-carved wood bowls from Moradabad, Agra, Khurja and Saharanpur — product development, QC and export documentation.",
    eyebrow: "Home Décor · Decorative objects",
    h1: "Decorative bowl sourcing from India.",
    primaryKeyword: "Decorative bowl sourcing India",
    secondaryKeywords: ["Decorative bowls wholesale India", "Brass bowls India", "Marble bowls India", "Hand-carved wood bowls India", "Hammered metal bowls India"],
    lede: "Decorative and centrepiece bowls are a category where Indian craft depth is immediately visible — hammered brass and copper from Moradabad, hand-carved mango and acacia wood from Saharanpur, white marble from Agra, hand-painted ceramic from Khurja and Jaipur. Asia Sourcing runs full-stack sourcing, development, QC and documentation for retail programmes targeting décor, tabletop and gifting lines.",
    subcategories: [
      "Hammered brass and copper bowls",
      "Hand-carved mango and acacia wood bowls",
      "White marble and marble-inlay bowls",
      "Hand-painted ceramic centrepiece bowls",
      "Mother-of-pearl inlay bowls",
      "Enamel-decorated metal bowls",
      "Terracotta and stoneware bowls",
    ],
    materials: ["Brass", "Copper", "Mango wood", "Acacia wood", "White marble", "Ceramic", "Mother-of-pearl inlay", "Terracotta"],
    finishes: ["Hammered", "Patinated", "Antique brass", "Food-safe lacquered interiors", "Natural oil-finished wood", "Hand-painted ceramic"],
    clusters: [
      { id: "moradabad", note: "brass and copper centrepiece bowls" },
      { id: "saharanpur", note: "carved wood bowls" },
      { id: "agra", note: "marble and inlay bowls" },
      { id: "jaipur", note: "ceramic and blue-pottery bowls" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Decorative versus food-contact",
        body: [
          "Many retail-tier bowls are specified decorative-only, but a non-trivial fraction of buyers want food-contact clearance for serving use. We split the SKU tree accordingly: decorative pieces take lacquered or patinated interior finishes without food-contact declarations; serving pieces take tin-lining on brass/copper and food-safe glaze on ceramic, with LFGB / FDA / EU 1935 declarations attached per shipment.",
        ],
      },
      { h2: "Carved-wood yield reality",
        body: [
          "Hand-carved mango-wood centrepiece bowls carry a 15-25% raw-stock yield loss factored into FOB; the first-order buyer who treats this as negotiable usually ends up with a late or shorted container. Our costing templates expose yield assumptions on day one and let buyers decide whether to pay for the yield or accept a lower-quality grading tier.",
        ],
      },
    ],
    faqs: [
      ["Are decorative bowls from India food-safe?", "Decorative-only unless specified otherwise. Serving-grade SKUs take tin-lined brass/copper interiors or food-safe glazed ceramic with LFGB / FDA / EU 1935 declarations attached per shipment."],
      ["What's the MOQ for hammered brass bowls?", "80-200 pieces per SKU per size, factory-dependent. Larger diameter pieces (>30 cm) have lower MOQs."],
      ["Can you source matching sets — bowl plus serving utensils?", "Yes. We develop set programmes across bowls, servers and placemats as a single collection; packaging development is integrated so retail-ready sets arrive in finished gift boxes."],
    ],
  },
  {
    slug: "decorative-boxes",
    group: "decorative",
    title: "Decorative Box Sourcing from India | Wood, Brass, Bone & Mother-of-Pearl | Asia Sourcing",
    metaDescription:
      "Decorative and storage box sourcing from India. Hand-carved wood, inlaid, brass-trimmed and mother-of-pearl boxes from Saharanpur, Jaipur and Moradabad — supplier verification, QC and export documentation.",
    eyebrow: "Home Décor · Decorative objects",
    h1: "Decorative box sourcing from India.",
    primaryKeyword: "Decorative box sourcing India",
    secondaryKeywords: ["Decorative boxes wholesale India", "Wooden boxes India", "Jewellery boxes India", "Mother-of-pearl boxes India", "Bone inlay boxes India"],
    lede: "India's decorative-box category sits across three categories of craft: carved wood from Saharanpur, inlay (bone, mother-of-pearl, marble) from Jaipur and Agra, and metal-trimmed boxes from Moradabad. Asia Sourcing operates programmes for retail décor, jewellery storage and gifting ranges, with full development, QC and documentation support.",
    subcategories: [
      "Hand-carved mango-wood boxes",
      "Bone and camel-bone inlay boxes",
      "Mother-of-pearl inlay boxes",
      "Brass-trimmed wooden boxes",
      "Jewellery and watch boxes with velvet or satin lining",
      "Marble inlay keepsake boxes",
      "Lacquered papier-mâché boxes from Kashmir",
    ],
    materials: ["Mango wood", "Sheesham", "Camel bone", "Mother-of-pearl", "Brass", "Marble", "Papier-mâché"],
    finishes: ["Antique-finished wood", "Hand-painted lacquer", "Hand-inlaid bone", "Mother-of-pearl mosaic", "Brass-cornered"],
    clusters: [
      { id: "saharanpur", note: "carved and inlaid wooden boxes" },
      { id: "jaipur", note: "bone and mother-of-pearl inlay" },
      { id: "moradabad", note: "brass-trimmed boxes" },
      { id: "kashmir", note: "lacquered papier-mâché boxes" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Hinge, lining and lock as the three defect gates",
        body: [
          "Our PSI protocol for decorative boxes runs three dedicated gates: hinge smoothness and alignment, lining adhesion and bubble-free seat, and (where fitted) lock engagement. These three account for over 80% of in-category rejections. New factories are brought online under a tightened inspection regime for the first three POs.",
        ],
      },
      { h2: "Programme packaging",
        body: [
          "Decorative boxes are often retail-ready SKUs — the retail package is as important as the product. We develop the retail carton (ribbon, insert, barcode, country-of-origin label, OBC overlay) alongside the box itself and specify both in the signed PPS. Changes to packaging after PPS trigger a documented change-order fee the buyer approves before production.",
        ],
      },
    ],
    faqs: [
      ["Can you source custom jewellery boxes with branded lining?", "Yes. Lining fabric, pigment-matched velvet or satin, logo embroidery or embossing, and branded ribbon are all developed in sampling before PO."],
      ["Which cluster for mother-of-pearl inlay?", "Jaipur is the primary cluster. Nagina (near Moradabad) is a secondary source for mother-of-pearl on wooden bases."],
      ["Is CITES wood a concern for Indian decorative boxes?", "Sheesham (Dalbergia sissoo) is CITES Appendix II; export documentation includes the CITES certificate when applicable. Mango and acacia are unrestricted."],
    ],
  },
  {
    slug: "photo-frames",
    group: "decorative",
    title: "Photo Frame Sourcing from India | Brass, Wood, Enamel & Bone Inlay | Asia Sourcing",
    metaDescription:
      "Photo frame sourcing from India. Brass, wood, enamel, bone-inlay and marble photo frames for retail and private-label programmes — product development, sampling, QC and export documentation.",
    eyebrow: "Home Décor · Decorative objects",
    h1: "Photo frame sourcing from India.",
    primaryKeyword: "Photo frame sourcing India",
    secondaryKeywords: ["Photo frames wholesale India", "Brass photo frames India", "Wooden picture frames India", "Enamel photo frames India", "Decorative frames India"],
    lede: "Photo and picture frames from India range from everyday brass and wood pieces to intricate enamel, bone-inlay and marble pieces for giftware programmes. Asia Sourcing manages development, standardisation of glass and backing, QC against drop and insert tests, and export documentation for retail and gifting programmes.",
    subcategories: [
      "Brass photo frames · plain, hammered, patinated",
      "Hand-carved mango-wood and acacia frames",
      "Enamel-decorated meenakari frames from Jaipur",
      "Bone-inlay and mother-of-pearl frames",
      "Marble-base and inlay frames from Agra",
      "Multi-aperture collage frames",
    ],
    materials: ["Brass", "Mango wood", "Acacia wood", "Enamel", "Camel bone", "Mother-of-pearl", "White marble"],
    finishes: ["Antique brass", "Hammered", "Hand-painted enamel", "Hand-carved wood"],
    clusters: [
      { id: "moradabad", note: "brass and metal frames" },
      { id: "jaipur", note: "enamel, bone and mother-of-pearl frames" },
      { id: "saharanpur", note: "carved wood frames" },
      { id: "agra", note: "marble frames" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Backing, glass and stand reality",
        body: [
          "Three failure points dominate PSI reject reports: (1) glass thickness consistency (2 mm vs 3 mm matters for shipping yield), (2) backing-board warp in high-humidity transit, and (3) easel-stand friction (stand collapses or fails to open). Our standard spec locks all three — minimum 2 mm float glass, kraft-laminated MDF backing, pivot-tested easel stands.",
        ],
      },
      { h2: "Gifting versus home-décor",
        body: [
          "Buyers split roughly 60/40 between gifting-ranges (retail-boxed, often with gift-message cards) and home-décor ranges (multi-aperture collage pieces, oversized statement frames). We split development accordingly — gifting lines take tighter packaging development; home-décor lines take tighter wall-mount and backing specification.",
        ],
      },
    ],
    faqs: [
      ["What's the MOQ for brass photo frames from India?", "100-250 pieces per SKU, factory-dependent. Hammered and patinated frames at the higher end."],
      ["Can you develop custom-sized frames?", "Yes. Aperture sizes are typically specified in inches for US and Australia buyers, millimetres for UK/EU. We develop against the buyer's standard frame-size matrix and lock backing and glass sizes to it."],
    ],
  },
  {
    slug: "bookends",
    group: "decorative",
    title: "Bookend Sourcing from India | Brass, Marble, Wood & Resin | Asia Sourcing",
    metaDescription:
      "Decorative bookend sourcing from India. Brass, marble, wood and resin bookends in abstract, animal and architectural forms — product development, QC and export documentation for retail and gifting.",
    eyebrow: "Home Décor · Decorative objects",
    h1: "Decorative bookend sourcing from India.",
    primaryKeyword: "Bookend sourcing India",
    secondaryKeywords: ["Decorative bookends India", "Brass bookends India", "Marble bookends India", "Resin bookends India"],
    lede: "Bookends have moved from utility to décor over the last decade — statement pieces in brass, marble, cast iron, mango wood and resin, often sold as sculptural pairs. Asia Sourcing runs the full programme from pattern development through retail-boxed packaging.",
    subcategories: [
      "Brass sculptural bookends",
      "Marble and marble-inlay bookends from Agra",
      "Mango-wood and acacia bookends",
      "Cast-iron bookends",
      "Resin bookends with brass or marble accents",
      "Figurative bookend pairs — architectural, animal, abstract",
    ],
    materials: ["Brass", "Marble", "Mango wood", "Cast iron", "Resin"],
    finishes: ["Antique brass", "Polished marble", "Patinated iron", "Hand-painted resin"],
    clusters: [
      { id: "moradabad", note: "brass and metal bookends" },
      { id: "agra", note: "marble bookends" },
      { id: "saharanpur", note: "carved wood bookends" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Weight is the spec",
        body: [
          "A bookend that doesn't hold books is a decoration. Our standard spec locks a minimum per-piece weight by material (brass solid vs hollow; marble vs resin) and runs a load-hold check at PSI — a tested row of hardbacks held against a vertical wobble test for 60 seconds. Buyers who skip this gate see return rates spike post-launch.",
        ],
      },
    ],
    faqs: [
      ["Can bookends be sold as pairs with mixed materials?", "Yes. Programme development includes paired SKUs — a marble piece paired with a brass piece, or an iron piece paired with wood — specified as a single retail SKU with combined pack."],
      ["What's the typical FOB India price range?", "Entry $5 per pair for small resin, up to $45 per pair for solid-brass sculptural pieces."],
    ],
  },

  // ───────────────────────── B · CANDLES & FRAGRANCE ─────────────────────────
  {
    slug: "candles",
    group: "candles",
    title: "Candle Sourcing from India | Soy, Beeswax, Paraffin & Scented Candles | Asia Sourcing",
    metaDescription:
      "Candle sourcing from India for international retailers. Soy, beeswax, paraffin, pillar, votive and scented candles — IFRA-compliant fragrance development, CPSC and EU safety-labelled, QC and export documentation.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Candle sourcing from India for retailers & private-label brands.",
    primaryKeyword: "Candle sourcing India",
    secondaryKeywords: ["Candle manufacturers India", "Private label candles India", "Scented candles wholesale India", "Soy candles India", "Beeswax candles India", "Pillar candles India", "Votive candles India"],
    lede: "India is now a serious global origin for private-label candles, with full capability across soy, soy-coconut blend, beeswax, paraffin and vegetable-wax formats, retail-grade fragrance development to IFRA Category-10, and the full retail-packaging stack. Asia Sourcing manages development, compliance (CPSC burn testing, EU safety labelling, Prop 65), QC and export documentation.",
    subcategories: [
      "Soy and soy-coconut blend candles",
      "Beeswax candles",
      "Paraffin pillar and taper candles",
      "Scented container candles — glass, ceramic, metal",
      "Votive, tealight and tea-light holders paired",
      "Floating and shaped decorative candles",
    ],
    materials: ["Soy wax", "Beeswax", "Paraffin", "Vegetable blend wax", "Glass vessels", "Ceramic vessels", "Metal vessels"],
    finishes: ["Natural uncoloured", "Dye-coloured", "Hand-dipped layered", "Metallic-leafed exteriors"],
    clusters: [
      { id: "noida", note: "candle manufacturing & fragrance labs" },
      { id: "delhi", note: "retail-ready candle private-label manufacturers" },
      { id: "moradabad", note: "metal and brass candle vessels" },
      { id: "khurja", note: "ceramic candle vessels (vendor-level)" },
    ],
    services: [
      { id: "product-development-india", label: "Fragrance & wick development" },
      { id: "product-testing-compliance-india", label: "CPSC / EU compliance" },
      { id: "quality-control-india", label: "Burn-test QC" },
      { id: "export-documentation-india", label: "Hazmat export documentation" },
    ],
    uniqueSections: [
      { h2: "What makes a sellable retail candle",
        body: [
          "Three variables separate a sellable retail candle from one that returns: clean burn (no soot or tunnelling), scent throw matching the brief, and label safety compliance. Our development workflow runs 12-hour burn tests on PPS, scent-throw assessment at cold and hot stages, and compliance labelling matched to each destination market (CPSC / ASTM F2417 for US, EN 15493 / EN 15494 for EU, consumer-safety stickers for AU and ZA).",
        ],
      },
      { h2: "Hazmat and shipping",
        body: [
          "Candles ship under flashpoint declarations — soy and beeswax typically non-hazmat, paraffin pillars sometimes classified. We handle the full declaration and insurance package per container, and our export-documentation service generates both the shipping manifest and the destination-market consumer-labelling pack as a single deliverable.",
        ],
      },
    ],
    faqs: [
      ["Can Asia Sourcing develop a custom fragrance for private-label candles?", "Yes. We work with IFRA Category-10 compliant fragrance houses in Noida and Delhi. Development is a 3-5 round process — brief, fragrance panels, cold and hot throw testing, finalisation, allergen declaration."],
      ["Do you provide CPSC compliance for US candle imports?", "Yes. ASTM F2417 stability and burn testing on candles, ASTM F2179 for secondary burn, and complete CPSC labelling (CA Prop 65 included) is part of the standard export package for US-bound shipments."],
      ["What's the MOQ for private-label scented candles?", "500-1,000 units per SKU for standard container candles. Larger retail chains routinely run 2,000-5,000-unit launches."],
      ["Can you source candles with branded packaging?", "Yes. Rigid gift boxes, kraft sleeves, wax-sealed labels, hot-foil retail stickers and barcode / country-of-origin labels are developed alongside the candle in sampling."],
    ],
  },
  {
    slug: "candle-holders",
    group: "candles",
    title: "Candle Holder Sourcing from India | Brass, Glass, Marble & Iron | Asia Sourcing",
    metaDescription:
      "Candle holder sourcing from India. Brass votive and pillar holders, hand-blown glass, marble, wrought iron, hurricane lamps and candelabras — Moradabad, Firozabad and Agra clusters.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Candle holder sourcing from India.",
    primaryKeyword: "Candle holder sourcing India",
    secondaryKeywords: ["Candle holders wholesale India", "Brass candle holders India", "Glass candle holders India", "Marble candle holders India", "Candelabra India", "Hurricane lamps India"],
    lede: "Candle holders are one of Moradabad's and Firozabad's largest export categories — brass votive and pillar holders, hand-blown glass hurricanes, hammered-metal lanterns, candelabras in brass and iron, marble and alabaster bases. Asia Sourcing supervises development through retail-ready delivery for every major format.",
    subcategories: [
      "Brass votive, tealight and pillar holders",
      "Hand-blown glass hurricane lamps and cloches",
      "Candelabras in brass, nickel and wrought iron",
      "Marble and alabaster candle bases",
      "Hammered-metal lanterns (indoor styling)",
      "Mother-of-pearl and bone-inlay holders",
    ],
    materials: ["Brass", "Nickel", "Hand-blown glass", "Wrought iron", "White marble", "Alabaster", "Mother-of-pearl"],
    finishes: ["Antique brass", "Patinated", "Nickel-polished", "Powder-coated iron", "Hand-cut glass"],
    clusters: [
      { id: "moradabad", note: "brass, nickel and metal candle holders" },
      { id: "firozabad", note: "hand-blown and cut-glass hurricanes" },
      { id: "agra", note: "marble and alabaster bases" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "The glass-fitment problem",
        body: [
          "Candle-holder programmes that pair a metal base with a glass hurricane fail routinely on fitment — the glass is slightly out of tolerance against the base, so either rattles or doesn't seat. Our DPI protocol runs an on-line fitment gauge before glass batches ship from Firozabad to the Moradabad assembly line; buyers who skip this step see rework costs eat the margin.",
        ],
      },
    ],
    faqs: [
      ["What's the MOQ for brass votive candle holders?", "100-200 pieces per SKU is standard; large tealight-holder runs often start at 500."],
      ["Can you source matching candle plus holder sets?", "Yes. We develop paired SKUs — scented candle in a bespoke brass holder, or glass hurricane with a pillar candle — as a single retail-boxed set."],
    ],
  },
  {
    slug: "candlesticks",
    group: "candles",
    title: "Candlestick Sourcing from India | Brass, Nickel, Marble & Glass | Asia Sourcing",
    metaDescription:
      "Candlestick and candelabra sourcing from India. Brass, nickel-plated, marble and glass candlesticks for retail and private-label programmes — product development, QC and export documentation.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Candlestick sourcing from India.",
    primaryKeyword: "Candlestick sourcing India",
    secondaryKeywords: ["Candlesticks wholesale India", "Brass candlesticks India", "Nickel candlesticks India", "Marble candlesticks India", "Candelabras India"],
    lede: "Candlesticks and candelabras are a staple Moradabad and Agra category — turned brass and nickel, marble bases, hand-blown glass stems, wrought-iron multi-arm candelabras. Asia Sourcing manages pattern development, mould approval, finish consistency and container-loading supervision.",
    subcategories: [
      "Turned brass candlesticks · pillar and taper",
      "Nickel-plated candlesticks",
      "Marble-base candlesticks",
      "Hand-blown glass candlesticks",
      "Wrought-iron multi-arm candelabras",
    ],
    materials: ["Brass", "Nickel-plated brass", "White marble", "Hand-blown glass", "Wrought iron"],
    finishes: ["Antique brass", "Polished nickel", "Polished marble", "Powder-coated iron"],
    clusters: [
      { id: "moradabad", note: "brass and nickel candlesticks" },
      { id: "agra", note: "marble candlesticks" },
      { id: "firozabad", note: "glass candlesticks" },
    ],
    services: [
      { id: "product-development-india", label: "Pattern development" },
      { id: "quality-control-india", label: "Finish-consistency QC" },
    ],
    uniqueSections: [
      { h2: "Taper fitment and dripcatch",
        body: [
          "Taper-candle fitment is the single most-returned fault in candlestick retail — the taper either doesn't seat or drips wax onto the surface. Our development spec locks the taper socket diameter to a specific standard (US 7/8″ or 22 mm EU) by region, and we audit the first production batch against both taper standards before releasing the PO.",
        ],
      },
    ],
    faqs: [
      ["What's the typical FOB India price for a brass candlestick?", "$3-$18 depending on height and complexity; polished nickel adds roughly 15-25% to the brass base."],
      ["Can you source candelabras for US buyers with Prop 65 compliance?", "Yes. Polished nickel over brass is Prop 65-compliant; we attach the material declaration and heavy-metal test report with every US-bound shipment."],
    ],
  },
  {
    slug: "lanterns",
    group: "candles",
    title: "Lantern Sourcing from India | Decorative, Indoor & Outdoor Lanterns | Asia Sourcing",
    metaDescription:
      "Decorative lantern sourcing from India for retail. Metal, brass, galvanised, hand-blown glass and Moroccan-style lanterns for indoor styling and outdoor décor — Moradabad and Firozabad clusters.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Decorative lantern sourcing from India.",
    primaryKeyword: "Decorative lantern sourcing India",
    secondaryKeywords: ["Decorative lanterns wholesale India", "Metal lanterns India", "Brass lanterns India", "Moroccan lanterns India", "Garden lanterns India", "Indoor lanterns India"],
    lede: "Decorative lanterns span indoor styling (brass, nickel, hammered-metal, cut-glass) and outdoor décor (galvanised, powder-coated iron, weather-sealed Moroccan-style with coloured glass). Asia Sourcing programmes typically blend both — a mixed container flows 15-30 SKUs across indoor and garden formats.",
    subcategories: [
      "Hammered brass and metal lanterns",
      "Cut-glass and hand-blown glass lanterns",
      "Moroccan-style perforated metal with coloured glass",
      "Galvanised and powder-coated outdoor lanterns",
      "Hurricane lanterns with glass chimneys",
      "Battery-LED and real-candle format pairs",
    ],
    materials: ["Brass", "Nickel", "Galvanised steel", "Powder-coated iron", "Hand-blown glass", "Coloured cut glass"],
    finishes: ["Antique brass", "Patinated", "Galvanised natural", "Powder-coated black / white / sage", "Hand-cut Moroccan patterns"],
    clusters: [
      { id: "moradabad", note: "brass and metal lanterns" },
      { id: "firozabad", note: "cut-glass and coloured-glass panels" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Weather-seal QC for outdoor lines" },
    ],
    uniqueSections: [
      { h2: "Indoor versus outdoor spec splits",
        body: [
          "Outdoor lanterns need weather-sealed hardware (zinc-plated screws, powder-coated interior), glazed glass or welded panel seams, and rust-prevention primer on the base metal. Our outdoor QC protocol runs a 48-hour salt-spray sample test before each new SKU's first PO. Buyers who treat indoor and outdoor as a single spec see rust failures within two seasons.",
        ],
      },
    ],
    faqs: [
      ["Can you source lanterns rated for outdoor use?", "Yes — zinc-plated hardware, powder-coat sealed, 48-hour salt-spray sample tested. Specify at brief whether a SKU is indoor-styling or weather-rated outdoor."],
      ["What's the MOQ for Moroccan-style lanterns?", "150-300 pieces per SKU, higher for coloured-glass panelled pieces due to the cutting-wastage factor."],
    ],
  },
  {
    slug: "home-fragrance",
    group: "candles",
    title: "Home Fragrance Sourcing from India | Reed Diffusers, Room Sprays, Incense | Asia Sourcing",
    metaDescription:
      "Home fragrance sourcing from India for retailers and brands. Reed diffusers, room sprays, scented sachets, incense and premium fragrance development — IFRA-compliant, EU and US safety-labelled.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Home fragrance sourcing from India.",
    primaryKeyword: "Home fragrance sourcing India",
    secondaryKeywords: ["Reed diffusers wholesale India", "Room sprays private label India", "Fragrance oils India", "Scented sachets India", "Incense sourcing India", "Home fragrance manufacturers India"],
    lede: "India is a growing global origin for private-label home fragrance — reed diffusers, room sprays, scented sachets, premium incense, and solid balms. Asia Sourcing supports development with IFRA-compliant fragrance houses in Noida and Delhi, retail packaging, CPSC and EU compliance, and consolidated export documentation.",
    subcategories: [
      "Reed diffusers · glass vessels, rattan reeds, 100-200 ml formats",
      "Room sprays · glass bottles with retail-ready triggers",
      "Scented sachets · drawer and wardrobe formats",
      "Premium incense · cone, stick, resin",
      "Solid fragrance balms and body-safe scented tins",
    ],
    materials: ["Fragrance oil (IFRA Cat-10)", "Hand-blown glass", "Rattan reeds", "Natural resins", "Food-grade carrier oils"],
    finishes: ["Clear and amber glass", "Hand-labelled retail-ready", "Branded gift boxes"],
    clusters: [
      { id: "noida", note: "fragrance manufacturing & packaging" },
      { id: "delhi", note: "premium incense and natural-resin" },
      { id: "firozabad", note: "hand-blown fragrance vessels (vendor)" },
    ],
    services: [
      { id: "product-development-india", label: "Fragrance development" },
      { id: "product-testing-compliance-india", label: "IFRA / CPSC / EU compliance" },
      { id: "export-documentation-india", label: "Hazmat documentation" },
    ],
    uniqueSections: [
      { h2: "Fragrance development is a programme, not an SKU",
        body: [
          "Fragrance development typically runs 4-8 rounds — the first round is a scent-direction fan, subsequent rounds are refinement, the final round is IFRA documentation and allergen declaration. Buyers often underestimate the time: a bespoke diffuser fragrance takes 10-14 weeks from brief to first PPS production. We lock the fragrance brief and the retail timeline on day one so launches don't slip.",
        ],
      },
      { h2: "Hazmat routing",
        body: [
          "Room sprays and some diffuser bases are classified as limited-quantity hazmat under IATA / IMDG. We handle the full UN3334 / UN1197 flashpoint and quantity declaration, consolidate hazmat shipments onto suitable vessels only, and attach the SDS and transport-emergency-contact sheet with every PO.",
        ],
      },
    ],
    faqs: [
      ["Can you develop a private-label reed diffuser range?", "Yes. Fragrance development, glass-vessel sourcing from Firozabad, rattan-reed supply, retail-ready labelling and branded gift boxes are all handled under one PO."],
      ["Are Indian-sourced room sprays compliant for EU retail?", "Yes. EU 1223 cosmetics-regulation labelling is required for body-contact scents; EN 15494 for ambient-use sprays. Our compliance team handles both tracks."],
      ["What's the MOQ for a bespoke fragrance?", "After fragrance lock, 1,000-2,000 units per SKU is the entry economic batch for diffusers; 2,000+ for sprays."],
    ],
  },
  {
    slug: "reed-diffusers",
    group: "candles",
    title: "Reed Diffuser Sourcing from India | Private Label & Retail | Asia Sourcing",
    metaDescription:
      "Reed diffuser sourcing from India for international retailers and private-label brands. Hand-blown glass bottles, rattan reeds, IFRA-compliant fragrance oils and retail-ready packaging.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Reed diffuser sourcing from India for retail & private label.",
    primaryKeyword: "Reed diffuser sourcing India",
    secondaryKeywords: ["Reed diffusers wholesale India", "Private label reed diffusers India", "Diffuser bottles India", "Fragrance diffusers India"],
    lede: "Reed diffusers are the most consistently repeating home-fragrance SKU in Indian retail programmes — 100 ml and 200 ml glass bottles from Firozabad, natural rattan reeds, IFRA-certified fragrance oils developed in Noida and Delhi labs, retail-ready labelling and branded gift packaging under one PO.",
    subcategories: [
      "100 ml, 150 ml and 200 ml retail-standard formats",
      "Hand-blown glass bottles — clear, amber, smoked",
      "Natural rattan reeds (6, 8 and 10-reed packs)",
      "IFRA Category-10 fragrance oils",
      "Retail-ready kraft or rigid-box packaging",
    ],
    materials: ["Hand-blown glass", "Rattan reeds", "Fragrance oil (IFRA Cat-10)", "Carrier solvents"],
    finishes: ["Clear glass", "Amber glass", "Smoked glass", "Hot-foil labels"],
    clusters: [
      { id: "noida", note: "fragrance labs" },
      { id: "firozabad", note: "hand-blown glass bottles" },
      { id: "delhi", note: "retail-ready assembly and packaging" },
    ],
    services: [
      { id: "product-development-india", label: "Fragrance development" },
      { id: "product-testing-compliance-india", label: "IFRA / EU compliance" },
    ],
    uniqueSections: [
      { h2: "The leak-and-evaporation gate",
        body: [
          "Two defects dominate retailer-return rates for diffusers: cap-seal leak during ocean transit and premature evaporation shortening retail shelf life. Our standard spec locks a tamper-evident foil seal under the retail cap, a double O-ring in the reed collar, and QC drop tests at PSI; evaporation half-life is validated against the signed PPS in a 60-day pre-shipment hold-back.",
        ],
      },
    ],
    faqs: [
      ["How long does a 200 ml reed diffuser last?", "Standard specification is 90-120 days of ambient diffusion from a 200 ml formulation, dependent on rattan-reed count and room ventilation. We benchmark actual half-life in PPS validation."],
      ["Can you develop vegan reed diffusers?", "Yes. All our standard formulations use plant-derived fragrance components; animal-origin fixatives are specified only on request."],
    ],
  },
  {
    slug: "incense-holders",
    group: "candles",
    title: "Incense Holder Sourcing from India | Brass, Wood, Ceramic & Stone | Asia Sourcing",
    metaDescription:
      "Incense holder and burner sourcing from India. Brass, hand-carved wood, soapstone and ceramic holders for sticks, cones and resin incense — product development, QC and export documentation.",
    eyebrow: "Home Décor · Candles & fragrance",
    h1: "Incense holder & burner sourcing from India.",
    primaryKeyword: "Incense holder sourcing India",
    secondaryKeywords: ["Incense holders wholesale India", "Brass incense burners India", "Soapstone incense holders India", "Cone incense holders India", "Resin incense burners India"],
    lede: "India is the natural origin for incense accessories — brass burners from Moradabad, hand-carved wood holders from Saharanpur, soapstone and marble bases from Agra, ceramic from Khurja. Programmes run as holders-only, holders-plus-incense pairs, or complete gifting bundles.",
    subcategories: [
      "Stick-incense wood and brass holders",
      "Cone-incense brass bowls and soapstone bases",
      "Resin-incense coal-burning brass bowls",
      "Hand-carved wooden ash-catchers",
      "Ceramic and soapstone pyramid burners",
    ],
    materials: ["Brass", "Mango wood", "Sheesham", "Soapstone", "Ceramic", "White marble"],
    finishes: ["Antique brass", "Carved wood with natural oil", "Polished soapstone", "Hand-painted ceramic"],
    clusters: [
      { id: "moradabad", note: "brass incense burners" },
      { id: "saharanpur", note: "carved wooden holders" },
      { id: "agra", note: "soapstone and marble bases" },
      { id: "jaipur", note: "ceramic holders" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Pairing with premium incense",
        body: [
          "Many retail programmes bundle the holder with a premium incense SKU — Indian temple-grade stick incense, resin-based frankincense, cold-processed cone. Our development team handles both tracks under one PO so the bundle lands as a single retail-ready set.",
        ],
      },
    ],
    faqs: [
      ["What's the MOQ for brass incense burners?", "150-300 pieces per SKU, factory-dependent."],
      ["Can you source holders paired with premium incense?", "Yes. We bundle holder plus incense SKUs with retail-ready gift packaging under a single PO."],
    ],
  },

  // ───────────────────────── C · TABLETOP & DINING ─────────────────────────
  {
    slug: "tabletop-decor",
    group: "tabletop",
    title: "Tabletop Décor Sourcing from India | Full Programme | Asia Sourcing",
    metaDescription:
      "Tabletop décor sourcing from India. Trays, bowls, coasters, placemats, napkin rings, table runners, centrepieces — brass, wood, marble, ceramic and textile from Moradabad, Jaipur, Agra and Panipat.",
    eyebrow: "Home Décor · Tabletop & dining",
    h1: "Tabletop décor sourcing from India.",
    primaryKeyword: "Tabletop décor sourcing India",
    secondaryKeywords: ["Table décor wholesale India", "Dining décor India", "Centrepiece sourcing India", "Table styling India"],
    lede: "Tabletop is a cross-cluster category — brass serving pieces from Moradabad, hand-carved wood boards and trays from Saharanpur, marble coasters and centrepieces from Agra, enamel and bone-inlay from Jaipur, placemats and napkin rings in textile and metal from Panipat. Asia Sourcing manages integrated development across all layers for retail and gifting programmes.",
    subcategories: [
      "Serving trays and bowls",
      "Coasters and placemats (hard and textile)",
      "Napkin rings and holders",
      "Table runners and linen",
      "Centrepieces — brass, marble, glass",
      "Barware accessories",
      "Utensil holders and caddies",
    ],
    materials: ["Brass", "Mango wood", "Marble", "Ceramic", "Hand-blown glass", "Woven cotton", "Jute", "Leather"],
    finishes: ["Antique brass", "Hammered metal", "Marble inlay", "Hand-painted meenakari", "Hand-woven textile"],
    clusters: [
      { id: "moradabad", note: "brass tabletop" },
      { id: "saharanpur", note: "wood tabletop" },
      { id: "agra", note: "marble tabletop" },
      { id: "jaipur", note: "enamel and inlay tabletop" },
      { id: "panipat", note: "textile tabletop" },
    ],
    services: [
      { id: "product-development-india", label: "Collection development" },
      { id: "quality-control-india", label: "Multi-material QC" },
    ],
    uniqueSections: [
      { h2: "Programme architecture",
        body: [
          "A typical first-container tabletop programme runs 40-80 SKUs blended across formats — a table-styling retailer will mix 15 serving pieces, 10 coaster-and-placemat SKUs, 8 napkin rings, 6 runners, 5 centrepieces. We architect the collection as a single palette (finish, colour, material blend) so the retail photography and merchandising hold together.",
        ],
      },
    ],
    faqs: [
      ["Can you develop a complete tabletop collection with matching finishes?", "Yes. Collection development runs across 40-80 SKUs in a single palette — same brass finish across serving pieces, matching marble inlay across coasters and centrepieces."],
      ["What's the typical lead time for a tabletop collection launch?", "16-22 weeks from brief lock to first-container PSI — the longer end for marble and enamel-heavy ranges."],
    ],
  },
  {
    slug: "coasters",
    group: "tabletop",
    title: "Coaster Sourcing from India | Marble, Wood, Brass & Leather | Asia Sourcing",
    metaDescription:
      "Coaster sourcing from India for retail. Marble, wood, brass, cork-backed and leather coasters — product development, QC and retail-ready packaging for international retailers.",
    eyebrow: "Home Décor · Tabletop & dining",
    h1: "Coaster sourcing from India.",
    primaryKeyword: "Coaster sourcing India",
    secondaryKeywords: ["Coasters wholesale India", "Marble coasters India", "Wooden coasters India", "Leather coasters India", "Brass coasters India"],
    lede: "Coasters are a programme category — most retail launches run 10-20 coaster SKUs across materials and sizes. India covers every material format: hand-cut marble from Agra, inlaid and etched brass from Moradabad, hand-carved wood from Saharanpur, embossed leather from Agra and Jaipur, hand-woven textile from Panipat.",
    subcategories: [
      "Marble coasters — plain, inlaid, etched",
      "Mango-wood and acacia coasters",
      "Brass coasters — etched, hammered, patinated",
      "Embossed and vegetable-tanned leather coasters",
      "Woven textile and jute coasters",
      "Cork-backed hybrid coasters",
    ],
    materials: ["White marble", "Mango wood", "Acacia", "Brass", "Vegetable-tanned leather", "Cotton weave", "Jute", "Cork"],
    finishes: ["Polished marble", "Natural wood oil", "Antique brass", "Hand-stitched leather"],
    clusters: [
      { id: "agra", note: "marble and leather coasters" },
      { id: "moradabad", note: "brass coasters" },
      { id: "saharanpur", note: "wood coasters" },
      { id: "panipat", note: "textile coasters" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Cork-backing and base-protection",
        body: [
          "Our standard spec locks a cork or felt base on marble and brass coasters to prevent surface scratching — the easiest post-sale complaint to design out at $0.02 of added material cost. Buyers who skip this gate accumulate return rates that eat the SKU margin by month four.",
        ],
      },
    ],
    faqs: [
      ["What's the MOQ for marble coasters from India?", "Sets of 4 or 6 at 300-500 sets per SKU is standard; smaller MOQs available for mixed container consolidation."],
      ["Can you source branded coaster sets with retail packaging?", "Yes. Retail boxes with inserts, hot-foil branding on leather coasters, laser-etched logos on marble and wood are all developed in sampling."],
    ],
  },
  {
    slug: "placemats",
    group: "tabletop",
    title: "Placemat Sourcing from India | Cotton, Jute, Leather & Beaded | Asia Sourcing",
    metaDescription:
      "Placemat sourcing from India for retailers. Hand-woven cotton, jute, leather, beaded and metallic placemats from Panipat, Jaipur and Delhi NCR — product development, QC and export documentation.",
    eyebrow: "Home Décor · Tabletop & dining",
    h1: "Placemat sourcing from India.",
    primaryKeyword: "Placemat sourcing India",
    secondaryKeywords: ["Placemats wholesale India", "Cotton placemats India", "Jute placemats India", "Beaded placemats India", "Leather placemats India"],
    lede: "Placemats sit across textile and craft clusters — hand-woven cotton and jute from Panipat, beaded and sequinned pieces from Jaipur and Delhi NCR, embossed leather from Agra, metallic braided and quilted formats from Delhi. Asia Sourcing manages development, pattern lock, QC and export documentation.",
    subcategories: [
      "Hand-woven cotton placemats",
      "Jute and sisal placemats",
      "Beaded and sequinned placemats",
      "Embossed and vegetable-tanned leather placemats",
      "Quilted and block-printed cotton placemats",
      "Metallic braided and foiled placemats",
    ],
    materials: ["Cotton", "Jute", "Sisal", "Leather", "Glass beads", "Sequins", "Cotton duck"],
    finishes: ["Natural fibres", "Block-print", "Hand-beaded", "Embossed leather"],
    clusters: [
      { id: "panipat", note: "cotton and jute placemats" },
      { id: "jaipur", note: "block-print and beaded" },
      { id: "delhi", note: "metallic and sequinned" },
    ],
    services: [
      { id: "product-development-india", label: "Textile development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Wash-fastness and shrinkage",
        body: [
          "Textile placemats carry two retail-level risks: colour-fastness (ISO 105-C06 washing) and shrinkage on first wash (ISO 3759). Our development spec locks tested parameters on PPS and we attach the wash-care label with every production SKU; our retail buyers specify laundering tolerance at brief so returns don't come back after the first wash cycle.",
        ],
      },
    ],
    faqs: [
      ["Can you source matching placemat and napkin sets?", "Yes. Placemat plus napkin plus runner development runs under one PO with matched block-prints or colourways."],
      ["What's the MOQ for block-printed cotton placemats?", "500-1,000 pieces per SKU, factory-dependent; larger for custom block-print carving."],
    ],
  },
  {
    slug: "serving-trays",
    group: "tabletop",
    title: "Serving Tray Sourcing from India | Wood, Brass, Marble & Enamel | Asia Sourcing",
    metaDescription:
      "Serving tray sourcing from India. Mango-wood, acacia, brass, marble and enamel serving trays with handles — product development, QC and export documentation for retail and hospitality.",
    eyebrow: "Home Décor · Tabletop & dining",
    h1: "Serving tray sourcing from India.",
    primaryKeyword: "Serving tray sourcing India",
    secondaryKeywords: ["Serving trays wholesale India", "Wooden serving trays India", "Brass serving trays India", "Marble serving trays India"],
    lede: "Serving trays overlap retail-décor and hospitality-FF&E programmes. Formats run from everyday mango-wood trays with brass handles to premium marble pieces for luxury-table styling. Asia Sourcing covers both — retail SKU development and hospitality contract runs under the same operation.",
    subcategories: [
      "Mango-wood and acacia serving trays with brass handles",
      "Brass serving trays — round, rectangular, oval",
      "Marble serving trays with inlay",
      "Enamel-painted serving trays",
      "Hospitality-grade stackable serving trays",
    ],
    materials: ["Mango wood", "Acacia", "Brass", "White marble", "Enamel on brass", "Leather handles"],
    finishes: ["Natural wood oil", "Burnt and whitewashed wood", "Antique brass", "Hand-painted enamel"],
    clusters: [
      { id: "saharanpur", note: "wood serving trays" },
      { id: "moradabad", note: "brass serving trays" },
      { id: "agra", note: "marble serving trays" },
    ],
    services: [
      { id: "hotel-project-procurement-india", label: "Hospitality procurement" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Food-contact and finish-safety",
        body: [
          "Serving trays carry food-contact spec requirements that decorative trays don't. Food-safe lacquers on wood, food-safe interior finishes on brass (tin-lining for serving-to-hot-food), LFGB / EU 1935 declarations attached per PO. We clarify at brief whether a SKU is served-food-direct or served-food-via-plate — the two drive different finish specs.",
        ],
      },
    ],
    faqs: [
      ["Can you source serving trays with branded burn-marks or embossing?", "Yes. Laser-engraved logos on wood, hot-foil debossing on leather handles, hand-engraved brass pieces for luxury runs."],
      ["What's the hospitality-grade MOQ?", "500+ per SKU for hotel-chain contract orders; we routinely coordinate 2,000-5,000-unit project runs for international hospitality groups."],
    ],
  },
  {
    slug: "table-runners",
    group: "tabletop",
    title: "Table Runner Sourcing from India | Cotton, Block-Print, Beaded | Asia Sourcing",
    metaDescription:
      "Table runner sourcing from India for retail and hospitality. Block-print cotton, jute, beaded, embroidered and metallic-foiled runners from Panipat and Jaipur — product development, QC and export documentation.",
    eyebrow: "Home Décor · Tabletop & dining",
    h1: "Table runner sourcing from India.",
    primaryKeyword: "Table runner sourcing India",
    secondaryKeywords: ["Table runners wholesale India", "Block print table runners India", "Beaded table runners India", "Cotton table runners India", "Jute table runners India"],
    lede: "Table runners are a weekly repeater on retail-décor shelves — hand-block-printed cotton from Jaipur, hand-woven cotton and jute from Panipat, beaded and sequinned statement pieces from Jaipur and Delhi, embroidered runners from Chikan-work artisans. Asia Sourcing manages development, pattern approval, wash-fastness testing and export documentation.",
    subcategories: [
      "Hand-block-printed cotton runners",
      "Hand-woven cotton and jute runners",
      "Beaded and sequinned statement runners",
      "Chikan-work and Kantha-embroidered runners",
      "Metallic-foiled and festive runners",
    ],
    materials: ["Cotton", "Jute", "Silk blend", "Glass beads", "Sequins", "Metallic foil"],
    finishes: ["Hand-block-print", "Kantha embroidery", "Chikan embroidery", "Hand-beaded"],
    clusters: [
      { id: "panipat", note: "cotton and jute runners" },
      { id: "jaipur", note: "block-print and beaded runners" },
    ],
    services: [
      { id: "product-development-india", label: "Textile development" },
      { id: "quality-control-india", label: "Wash-fastness QC" },
    ],
    uniqueSections: [
      { h2: "Pattern lock discipline",
        body: [
          "Hand-block-printed runners run a well-known risk: pattern drift between the signed sample and the production run as factory blocks age and new blocks are carved. Our DPI protocol runs an on-line pattern-match check at 30% completion against the signed PPS; any production falling outside tolerance is pulled before the full lot proceeds.",
        ],
      },
    ],
    faqs: [
      ["What's the typical MOQ for block-printed runners?", "500-800 pieces per SKU, factory-dependent."],
      ["Can you source custom block-prints?", "Yes. New-block carving adds 2-3 weeks and roughly $200-$600 per unique block depending on complexity, charged separately from unit cost."],
    ],
  },
  {
    slug: "napkin-rings",
    group: "tabletop",
    title: "Napkin Ring Sourcing from India | Brass, Beaded, Wood & Marble | Asia Sourcing",
    metaDescription:
      "Napkin ring sourcing from India for retail and hospitality. Brass, beaded, carved wood, bone-inlay and marble napkin rings — product development, QC and export documentation.",
    eyebrow: "Home Décor · Tabletop & dining",
    h1: "Napkin ring sourcing from India.",
    primaryKeyword: "Napkin ring sourcing India",
    secondaryKeywords: ["Napkin rings wholesale India", "Beaded napkin rings India", "Brass napkin rings India", "Carved wood napkin rings India"],
    lede: "Napkin rings carry category-leading margin because the material cost is low and perceived craft value is high — Moradabad brass, beaded Jaipur, carved mango-wood from Saharanpur, bone-inlay from Jaipur, marble from Agra. Asia Sourcing runs these as collection SKUs sold in sets of 4, 6 or 8.",
    subcategories: [
      "Hammered and etched brass napkin rings",
      "Beaded and sequinned napkin rings",
      "Hand-carved mango-wood napkin rings",
      "Bone and mother-of-pearl inlay napkin rings",
      "Marble and marble-inlay napkin rings",
    ],
    materials: ["Brass", "Mango wood", "Camel bone", "Mother-of-pearl", "Glass beads", "White marble"],
    finishes: ["Antique brass", "Hand-beaded", "Carved and natural-finished wood"],
    clusters: [
      { id: "moradabad", note: "brass napkin rings" },
      { id: "saharanpur", note: "wood napkin rings" },
      { id: "jaipur", note: "beaded and inlay napkin rings" },
      { id: "agra", note: "marble napkin rings" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Set-pack discipline",
        body: [
          "Set-of-4 and set-of-6 napkin rings have a specific retail-pack risk: colour or finish drift between units in the same set. Our PSI protocol sorts and matches units within sets before carton packing — a hygiene step surprisingly often skipped at factory level, and the single highest return driver for the category.",
        ],
      },
    ],
    faqs: [
      ["Can you source napkin rings matched to a tablecloth or runner?", "Yes. Napkin-ring colourways are typically developed in parallel with the runner / napkin set-pack palette."],
      ["What's the typical FOB India for a brass napkin-ring set of 4?", "$3-$8 per set depending on finish complexity; beaded sets run $4-$12."],
    ],
  },

  // ───────────────────────── D · GARDEN & OUTDOOR ─────────────────────────
  {
    slug: "hummingbird-feeders",
    group: "garden",
    title: "Hummingbird Feeder Sourcing from India | Glass, Metal, Decorative | Asia Sourcing",
    metaDescription:
      "Hummingbird feeder sourcing from India for North American and European garden retailers. Hand-blown glass, brass and copper, weather-sealed decorative feeders — product development, QC and export documentation.",
    eyebrow: "Home Décor · Garden & outdoor",
    h1: "Hummingbird feeder sourcing from India.",
    primaryKeyword: "Hummingbird feeder sourcing India",
    secondaryKeywords: ["Hummingbird feeders wholesale India", "Decorative hummingbird feeders India", "Glass hummingbird feeders India"],
    lede: "Hummingbird feeders are a specialist North-American garden category where India offers depth — Firozabad hand-blown glass bowls, Moradabad brass and copper perches and feeding ports, weather-sealed assembly in Moradabad. Asia Sourcing manages the full development stack with specialist US bird-feeding compliance.",
    subcategories: [
      "Hand-blown glass feeders · nectar bowls, drip bottles",
      "Decorative brass and copper feeders",
      "Weather-sealed hanging feeders",
      "Multi-port and single-port feeder formats",
      "Perch-with-feeder combined designs",
    ],
    materials: ["Hand-blown glass", "Brass", "Copper", "Powder-coated steel", "Food-safe silicone seals"],
    finishes: ["Clear and coloured glass", "Antique brass", "Hammered copper", "Powder-coated weatherproof"],
    clusters: [
      { id: "firozabad", note: "hand-blown glass feeder bowls" },
      { id: "moradabad", note: "metal perches, ports and assembly" },
    ],
    services: [
      { id: "product-development-india", label: "Product development" },
      { id: "quality-control-india", label: "Weather-seal and leak QC" },
    ],
    uniqueSections: [
      { h2: "The leak-and-ant gate",
        body: [
          "Two defects dominate hummingbird-feeder returns: nectar leaks under warming daytime temperatures (bad silicone seals), and ant invasion (poorly-designed moat or missing ant guard). Our development spec locks food-safe silicone seals and specifies an ant-moat or ant-guard integrated into the hanger. First-batch PSI runs a 24-hour leak test on sampled units.",
        ],
      },
      { h2: "US market context",
        body: [
          "US hummingbird-feeder retail is dominated by hobbyist buyers who return products aggressively on functional failure. We spec every US-bound SKU for CPSIA compliance (no lead-paint on feeding surfaces), Prop 65 compliance (brass tin-lined where contacting nectar), and consumer-safety labelling. Lead times to US East Coast via FOB Nhava Sheva are 32-45 days.",
        ],
      },
    ],
    faqs: [
      ["Are Indian-sourced hummingbird feeders CPSIA-compliant?", "Yes. CPSIA tested, Prop 65 compliant with tin-lined brass or coated food-contact surfaces, consumer-safety labelling attached."],
      ["What's the MOQ for a decorative brass hummingbird feeder?", "200-500 pieces per SKU, factory-dependent."],
      ["Can you develop a custom feeder design?", "Yes. Full workflow from brief to PPS; typical lead time 8-12 weeks given the glass-plus-metal integration."],
    ],
  },

  // ───────────────────────── E · SOFT FURNISHINGS ─────────────────────────
  {
    slug: "cushion-covers",
    group: "textiles",
    title: "Cushion Cover Sourcing from India | Block-Print, Velvet, Embroidered | Asia Sourcing",
    metaDescription:
      "Cushion cover sourcing from India for retailers and private-label brands. Hand-block-print, velvet, embroidered, Kantha and Kilim cushion covers from Panipat and Jaipur — development, QC and export documentation.",
    eyebrow: "Home Décor · Soft furnishings",
    h1: "Cushion cover sourcing from India.",
    primaryKeyword: "Cushion cover sourcing India",
    secondaryKeywords: ["Cushion covers wholesale India", "Block print cushion covers India", "Velvet cushion covers India", "Embroidered cushion covers India", "Kantha cushion covers India"],
    lede: "Cushion covers are a weekly-repeating retail category — hand-block-printed cotton from Jaipur, cotton-velvet from Panipat, Kantha-stitched and crewel-embroidered pieces, Kilim-woven covers from Panipat and Bhadohi. Asia Sourcing manages development, pattern lock, zipper and seam QC and wash-fastness testing.",
    subcategories: [
      "Hand-block-printed cotton covers",
      "Cotton-velvet solid and jacquard",
      "Kantha-stitched hand-quilted covers",
      "Crewel-work and chainstitch embroidery",
      "Kilim-woven wool and cotton covers",
      "Mughal and Suzani embroidered covers",
    ],
    materials: ["Cotton", "Cotton velvet", "Linen", "Wool", "Silk blend"],
    finishes: ["Hand-block-print", "Hand-embroidered", "Hand-woven Kilim", "Natural dyed"],
    clusters: [
      { id: "panipat", note: "velvet, cotton and Kilim covers" },
      { id: "jaipur", note: "block-print and embroidered covers" },
      { id: "bhadohi", note: "hand-woven Kilim" },
    ],
    services: [
      { id: "product-development-india", label: "Textile development" },
      { id: "quality-control-india", label: "Seam, zipper and wash-fastness QC" },
    ],
    uniqueSections: [
      { h2: "The zipper and seam gate",
        body: [
          "Cushion covers return for three reasons: zipper failure, seam blowout under cushion-insert fill, and colour-fastness. Our standard spec locks YKK or equivalent zippers, double-stitched top-and-side seams at 10 stitches per inch, and ISO 105-C06 colour-fastness testing. The seam gate reduces returns by 60% versus programmes that spec single-stitched seams.",
        ],
      },
    ],
    faqs: [
      ["Can you source cushion covers in GOTS-certified cotton?", "Yes. GOTS-certified cotton available for private-label programmes targeting EU retailers who require it; adds roughly 10-18% to the FOB price."],
      ["What's the MOQ for block-printed cushion covers?", "500-1,000 pieces per SKU; higher for custom-carved block patterns."],
      ["Do you supply cushion covers with inner fillers?", "Covers only is the standard; fillers can be included but typically ship as a parallel SKU due to volumetric efficiency on container loading."],
    ],
  },

  // ───────────────────────── F · BATHROOM & WELLNESS ─────────────────────────
  {
    slug: "bathroom-accessories",
    group: "bathroom",
    title: "Bathroom Accessories Sourcing from India | Brass, Marble, Ceramic | Asia Sourcing",
    metaDescription:
      "Bathroom accessories sourcing from India for retailers and hospitality. Brass, marble, ceramic and stone soap dispensers, dishes, tumblers, towel bars and storage — product development, QC and export documentation.",
    eyebrow: "Home Décor · Bathroom & wellness",
    h1: "Bathroom accessories sourcing from India.",
    primaryKeyword: "Bathroom accessories sourcing India",
    secondaryKeywords: ["Bathroom accessories wholesale India", "Brass bathroom accessories India", "Marble bathroom accessories India", "Ceramic bathroom accessories India", "Bathroom sets India"],
    lede: "Bathroom accessories are a strong Indian export category — Moradabad brass fittings and accessories, Agra white-marble pieces, Khurja and Jaipur ceramic, soapstone from Agra, teak and wood pieces from Saharanpur. Asia Sourcing manages retail-grade and hospitality-contract programmes with full compliance and documentation.",
    subcategories: [
      "Brass bathroom accessories — hooks, bars, holders",
      "Marble soap dishes, tumblers, toothbrush holders",
      "Ceramic bathroom sets",
      "Soapstone bath accessories",
      "Teak and wood bath storage",
      "Full bathroom set programmes (6-8 piece)",
    ],
    materials: ["Brass", "Nickel-plated brass", "White marble", "Soapstone", "Ceramic", "Teak", "Mango wood"],
    finishes: ["Antique brass", "Polished nickel", "Hammered", "Polished marble", "Hand-painted ceramic"],
    clusters: [
      { id: "moradabad", note: "brass bathroom accessories" },
      { id: "agra", note: "marble and soapstone pieces" },
      { id: "jaipur", note: "ceramic and painted pieces" },
      { id: "saharanpur", note: "teak and wood storage" },
    ],
    services: [
      { id: "hotel-project-procurement-india", label: "Hospitality procurement" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "Hospitality versus retail",
        body: [
          "Bathroom-accessory programmes split sharply between retail (gift-oriented, 1-2 piece purchases) and hospitality (6-8 piece coordinated sets per room, 100-500 room contract runs). We handle both: retail SKUs develop around gift packaging; hospitality contracts develop around durability (brass + marble combinations, hotel-grade finishes, replacement-order rhythm)."
        ],
      },
      { h2: "Weight is specification",
        body: [
          "Marble bathroom pieces are often spec'd by weight — a 3 kg marble soap dish signals premium; a 1 kg piece signals budget. Our weight spec on every PPS prevents factory substitution to lighter-weight marble under cost pressure, a problem particularly visible in budget-tier programmes."
        ],
      },
    ],
    faqs: [
      ["Can you source complete bathroom accessory sets for hotels?", "Yes. 6-8 piece coordinated sets (soap dispenser, toothbrush holder, tumbler, soap dish, cotton jar, waste bin, tissue cover, tray) developed as a single hospitality SKU, 100-500 room contract runs supported."],
      ["Are Indian brass bathroom accessories corrosion-resistant?", "Nickel-plated brass is corrosion-resistant to international standard; antique-finished brass has a natural patina and is suitable for low-humidity bathrooms; we specify finish by destination climate at brief."],
      ["What's the MOQ for marble bathroom sets?", "100-200 sets per SKU for retail; hospitality contracts have no fixed minimum — project scale drives the economics."],
    ],
  },
  {
    slug: "soap-dispensers",
    group: "bathroom",
    title: "Soap Dispenser Sourcing from India | Brass, Marble, Ceramic | Asia Sourcing",
    metaDescription:
      "Soap dispenser sourcing from India for retail and hospitality. Brass, marble, ceramic and glass soap dispensers with pump mechanisms — product development, pump reliability QC and export documentation.",
    eyebrow: "Home Décor · Bathroom & wellness",
    h1: "Soap dispenser sourcing from India.",
    primaryKeyword: "Soap dispenser sourcing India",
    secondaryKeywords: ["Soap dispensers wholesale India", "Brass soap dispensers India", "Marble soap dispensers India", "Ceramic soap dispensers India"],
    lede: "Soap dispensers combine a craft vessel (brass, marble, ceramic, hand-blown glass) with an engineered pump mechanism. Pump reliability is the category's single most-returned fault — Asia Sourcing specifies and verifies pump mechanisms separately from the vessel in every PPS.",
    subcategories: [
      "Hammered brass soap dispensers",
      "Marble soap dispensers from Agra",
      "Hand-painted ceramic dispensers",
      "Hand-blown glass dispensers",
      "Foam-pump and lotion-pump mechanisms",
    ],
    materials: ["Brass", "White marble", "Ceramic", "Hand-blown glass", "ABS pump"],
    finishes: ["Antique brass", "Polished nickel", "Polished marble", "Hand-painted ceramic"],
    clusters: [
      { id: "moradabad", note: "brass dispensers" },
      { id: "agra", note: "marble dispensers" },
      { id: "firozabad", note: "glass vessels" },
    ],
    services: [
      { id: "product-development-india", label: "Pump mechanism QA" },
      { id: "quality-control-india", label: "Quality control" },
    ],
    uniqueSections: [
      { h2: "The pump gate",
        body: [
          "Our standard spec runs a 1,000-cycle pump test on every PPS unit before production release. Pumps are sourced from pre-approved vendors (ABS resin, stainless-steel spring) and swapped on first failure. Buyers who skip this gate see 15-25% retail-return rates on the category.",
        ],
      },
    ],
    faqs: [
      ["Are the pumps on your soap dispensers warrantied?", "Our standard pump passes 1,000-cycle PPS testing. Retail-tier pumps carry a 6-month replacement warranty; hospitality-tier pumps carry 12 months."],
      ["Can you source soap-dispenser-plus-dish paired sets?", "Yes. Dispenser plus soap dish plus toothbrush holder as a coordinated bathroom trio is a standard retail SKU."],
    ],
  },
  {
    slug: "bathroom-storage",
    group: "bathroom",
    title: "Bathroom Storage Sourcing from India | Wood, Rattan, Metal | Asia Sourcing",
    metaDescription:
      "Bathroom storage sourcing from India. Teak, mango-wood, rattan, powder-coated metal and bamboo storage caddies, shelves and baskets — product development, QC and export documentation.",
    eyebrow: "Home Décor · Bathroom & wellness",
    h1: "Bathroom storage sourcing from India.",
    primaryKeyword: "Bathroom storage sourcing India",
    secondaryKeywords: ["Bathroom storage wholesale India", "Teak bathroom storage India", "Rattan bathroom baskets India", "Wooden bathroom shelves India"],
    lede: "Bathroom storage pulls from several clusters — teak and mango-wood caddies and shelves from Saharanpur, hand-woven rattan and bamboo baskets from Delhi NCR and Moradabad, powder-coated metal storage from Delhi. Humidity-resistance is the single spec that separates sellable bathroom storage from furniture that warps in month three.",
    subcategories: [
      "Teak bathroom shelves and caddies",
      "Mango-wood storage boxes with lids",
      "Rattan and bamboo bathroom baskets",
      "Powder-coated metal storage",
      "Wall-mounted bathroom shelves",
    ],
    materials: ["Teak", "Mango wood", "Rattan", "Bamboo", "Powder-coated steel"],
    finishes: ["Natural teak oil", "Water-sealed lacquer", "Natural rattan", "Powder-coated weather-sealed"],
    clusters: [
      { id: "saharanpur", note: "wood bathroom storage" },
      { id: "delhi", note: "rattan and metal storage" },
      { id: "moradabad", note: "hand-woven baskets" },
    ],
    services: [
      { id: "product-development-india", label: "Humidity-sealed development" },
      { id: "quality-control-india", label: "Humidity-resistance QC" },
    ],
    uniqueSections: [
      { h2: "Humidity is the only spec that matters",
        body: [
          "Bathroom storage lives in a 60-90% humidity environment for its entire life. Wood pieces warp, metal pieces rust, rattan swells and splits. Our development spec locks a water-sealed lacquer on wood, a zinc-plated and powder-coated finish on metal, and a stabilised-fibre specification on rattan. First-batch QC runs a 72-hour humidity-chamber test on sampled units before PO release.",
        ],
      },
    ],
    faqs: [
      ["Can your bathroom storage withstand high humidity?", "Yes. Our water-sealed-lacquer wood, powder-coated metal and stabilised-fibre rattan all pass a 72-hour humidity chamber test at 85% RH before PO release."],
      ["Is teak sustainably sourced?", "Our teak comes from certified plantations in Myanmar and Africa via established importers; FSC Chain-of-Custody available on request."],
    ],
  },

  // ───────────────────────── G · SEASONAL DÉCOR ─────────────────────────
  {
    slug: "christmas-decor",
    group: "seasonal",
    title: "Christmas Décor Sourcing from India | Ornaments, Stockings, Table | Asia Sourcing",
    metaDescription:
      "Christmas décor sourcing from India for international retailers. Glass ornaments, embroidered stockings, metallic table décor and velvet ribbons — product development, QC and export documentation for seasonal programmes.",
    eyebrow: "Home Décor · Seasonal décor",
    h1: "Christmas décor sourcing from India.",
    primaryKeyword: "Christmas décor sourcing India",
    secondaryKeywords: ["Christmas décor wholesale India", "Christmas ornaments India", "Christmas stockings India", "Glass ornaments India", "Festive décor India"],
    lede: "India is a serious global origin for Christmas décor — Firozabad hand-blown glass ornaments and baubles, embroidered and velvet stockings from Jaipur and Panipat, metallic table décor from Moradabad, hand-crafted tree-toppers and garlands. Asia Sourcing manages the seasonal programme architecture — capacity booking by April, PSI by August, container loading by September for an October-November landing.",
    subcategories: [
      "Hand-blown glass ornaments and baubles",
      "Embroidered and sequinned Christmas stockings",
      "Metallic table décor — brass stars, candle rings",
      "Hand-crafted tree-toppers and finials",
      "Velvet and embroidered Christmas ribbons",
      "Decorative garlands",
    ],
    materials: ["Hand-blown glass", "Cotton velvet", "Metallic foil", "Brass", "Glass beads", "Sequins"],
    finishes: ["Mouth-blown glass", "Hand-painted ornament", "Metallic foil", "Hand-embroidered"],
    clusters: [
      { id: "firozabad", note: "glass ornaments and baubles" },
      { id: "jaipur", note: "embroidered stockings and tree décor" },
      { id: "moradabad", note: "metallic table décor" },
      { id: "panipat", note: "velvet stockings and ribbons" },
    ],
    services: [
      { id: "product-development-india", label: "Seasonal collection development" },
      { id: "quality-control-india", label: "Seasonal programme QC" },
    ],
    uniqueSections: [
      { h2: "Seasonal programme discipline",
        body: [
          "Christmas is a date-locked category — a container landing a week late is unsold inventory. We lock the full programme in Q1: capacity booking in March-April, pattern and finish lock by May, PPS by June, production June-July, PSI August, container loading September. Buyers who come to us in June for a December landing are too late.",
        ],
      },
      { h2: "Glass ornament breakage",
        body: [
          "Hand-blown glass ornaments carry the category's highest breakage risk. Our standard spec runs individual cell packaging (12-ornament egg-crate inserts), 60 cm drop testing per master carton, and container loading supervised to pack-high / weight-low discipline. Programmes we supervise typically clear below 0.5% in-transit breakage.",
        ],
      },
    ],
    faqs: [
      ["When should I lock a Christmas programme for October landing?", "By end of March for capacity booking; patterns locked by May; PPS by June; production June-August; PSI August; container loading September. Buyers who come in June are too late."],
      ["What's the breakage rate on hand-blown glass ornaments from India?", "Programmes we supervise typically clear below 0.5% in-transit breakage with egg-crate cell packaging and supervised container loading."],
    ],
  },
  {
    slug: "christmas-ornaments",
    group: "seasonal",
    title: "Christmas Ornament Sourcing from India | Hand-Blown Glass, Metal, Fabric | Asia Sourcing",
    metaDescription:
      "Christmas ornament sourcing from India for retailers. Hand-blown glass baubles from Firozabad, embroidered fabric ornaments and metallic ornaments — development, QC and export documentation.",
    eyebrow: "Home Décor · Seasonal décor",
    h1: "Christmas ornament sourcing from India.",
    primaryKeyword: "Christmas ornament sourcing India",
    secondaryKeywords: ["Christmas ornaments wholesale India", "Hand blown glass ornaments India", "Christmas baubles India", "Fabric ornaments India"],
    lede: "Firozabad is one of the world's major origins for hand-blown Christmas baubles and ornaments. Asia Sourcing manages development, pattern approval, hand-painted finish consistency, cell-packaging specification and container loading supervision for seasonal programmes landing October-November.",
    subcategories: [
      "Hand-blown glass baubles · plain, hand-painted, glitter-coated",
      "Fabric ornaments · embroidered, sequinned, beaded",
      "Metallic ornaments — brass, copper, pewter",
      "Wooden carved ornaments from Saharanpur",
      "Multi-finish mixed-ornament collections",
    ],
    materials: ["Hand-blown glass", "Cotton", "Velvet", "Brass", "Copper", "Mango wood"],
    finishes: ["Mouth-blown glass", "Hand-painted ornament", "Glitter-coated", "Hand-embroidered"],
    clusters: [
      { id: "firozabad", note: "hand-blown glass ornaments" },
      { id: "jaipur", note: "fabric and embroidered ornaments" },
      { id: "moradabad", note: "metallic ornaments" },
    ],
    services: [
      { id: "product-development-india", label: "Seasonal product development" },
      { id: "quality-control-india", label: "Finish-consistency and breakage QC" },
    ],
    uniqueSections: [
      { h2: "The finish-consistency gate",
        body: [
          "Hand-painted ornaments carry a specific defect: finish drift between units in a 12-ornament retail pack. Our PSI sorts units within packs to colour-match tolerance before carton closure. Buyers who skip this gate see 15-25% within-pack colour drift — the single biggest retail-return driver in the category.",
        ],
      },
    ],
    faqs: [
      ["Can you source custom-painted ornaments with a brand design?", "Yes. New ornament patterns and hand-painted designs are developed in sampling; typical lead time 10-14 weeks from brief to PPS."],
      ["What's the MOQ for hand-blown Christmas ornaments?", "500-1,000 pieces per SKU per colourway. Mixed-colour collections consolidate across SKUs for container optimisation."],
    ],
  },
];

/** Lookup helpers. */
export const getHomeDecorCategory = (slug) =>
  HOME_DECOR_CATEGORIES.find((c) => c.slug === slug);

export const HOME_DECOR_SLUGS = HOME_DECOR_CATEGORIES.map((c) => c.slug);


/**
 * Sprint 28 · Nav mega-menu + hub directory helpers.
 * DECOR_GROUP_ORDER follows the buyer brief (Seasonal décor appended last).
 */
export const decorSlugLabel = (slug) =>
  slug.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");

export const DECOR_GROUP_ORDER = [
  "decorative",
  "candles",
  "tabletop",
  "bathroom",
  "garden",
  "textiles",
  "seasonal",
];

export const DECOR_MENU = DECOR_GROUP_ORDER
  .map((gid) => {
    const group = HOME_DECOR_GROUPS.find((g) => g.id === gid);
    return { ...group, items: HOME_DECOR_CATEGORIES.filter((c) => c.group === gid) };
  })
  .filter((g) => g.items.length > 0);
